/**
 * Erro de API do lado do navegador.
 *
 * O quê: o tipo de exceção que o cliente HTTP lança quando a API responde
 * fora da faixa de sucesso, ou quando a requisição nem chega a sair.
 *
 * Como: carrega o mesmo `codigo` que o servidor emitiu, o que permite à
 * interface decidir comportamento sem inspecionar texto — reautenticar em
 * `NAO_AUTENTICADO`, mostrar erro por campo em `DADOS_INVALIDOS`, e assim por
 * diante. Mensagem é para o usuário ler; código é para o programa decidir.
 * Trocar os papéis é como se acaba com um `if` comparando string traduzida.
 *
 * Utiliza: `CODIGOS_ERRO` de `@concierge/shared`.
 */
import { CODIGOS_ERRO, type CodigoErro } from '@concierge/shared';

/** Falha de uma chamada à API, já traduzida para o contrato. */
export class ErroDaApi extends Error {
  /** Código estável do contrato. Base para qualquer decisão da interface. */
  readonly codigo: CodigoErro;

  /** Status HTTP recebido. Zero quando a requisição não chegou a sair. */
  readonly status: number;

  /** Erros por campo, no formato de caminho que o react-hook-form entende. */
  readonly campos?: Record<string, string[]>;

  /**
   * @param codigo - Código devolvido pela API.
   * @param status - Status HTTP, ou zero em falha de rede.
   * @param mensagem - Texto em português, próprio para exibir ao usuário.
   * @param campos - Mapa de caminho do campo para as mensagens dele.
   */
  constructor(
    codigo: CodigoErro,
    status: number,
    mensagem: string,
    campos?: Record<string, string[]>,
  ) {
    super(mensagem);
    this.name = 'ErroDaApi';
    this.codigo = codigo;
    this.status = status;
    this.campos = campos;
  }
}

/**
 * Erro para quando a requisição não chegou ao servidor.
 *
 * Sem rede, sem DNS ou com a requisição cancelada, não há status nem código
 * vindos da API — mas o resto da aplicação não deveria precisar distinguir
 * essa origem. Encaixar no mesmo tipo, com status zero, mantém um caminho só
 * de tratamento de erro na interface.
 *
 * @returns Erro pronto para lançar, com status 0.
 */
export function erroDeRede() {
  return new ErroDaApi(
    CODIGOS_ERRO.ERRO_INTERNO,
    0,
    'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.',
  );
}
